import {
  ArrowRight2,
  Edit2,
  type Icon,
  Lock,
  Logout,
  Message,
  Notification,
  ProgrammingArrows,
  Setting2,
  ShieldTick,
  Star1,
} from 'iconsax-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLoaderData, useMatch, useNavigate } from 'react-router';
import QrCodeImage from '@/assets/images/icons/qr-code.svg?react';
import AppCell from '@/components/common/app-cell';
import type { ModalAction } from '@/components/common/modal/modal';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import QrCodeModal from '@/components/layouts/qr-code-modal';
import UserHero from '@/components/user/user-hero';
import useUserPlatform, { useIsDesktop } from '@/hooks/use-user-platform';
import useSignOut from '@/hooks/users/use-sign-out';
import useUserInfo from '@/hooks/users/use-user-info';
import ROUTES, { type PromiseLoaderReturnType } from '@/routes';
import { makePrompt } from '@/stores/prompter';

export default function MyIndexRoute() {
  const loaderData = useLoaderData<PromiseLoaderReturnType>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: userInfo } = useUserInfo();
  const { mutate: signOut } = useSignOut();
  const { isEmbedded } = useUserPlatform();
  const isDesktop = useIsDesktop();
  const qrCodeModal = useRef<ModalAction>(null);
  const menuItems: Readonly<Array<{ key: string; icon: Icon; href: string; hidden?: boolean }>> = [
    { icon: Star1, key: 'collections', href: ROUTES.MY.COLLECTIONS.$path() },
    { icon: Notification, key: 'notifications', href: ROUTES.MY.SETTINGS.NOTIFICATIONS.$path() },
    { icon: Message, key: 'messaging', href: ROUTES.MY.SETTINGS.MESSAGING.$path() },
    { icon: Lock, key: 'privacy', href: ROUTES.MY.SETTINGS.PRIVACY.$path() },
    { icon: ShieldTick, key: 'security', href: ROUTES.MY.SETTINGS.SECURITY.$path(), hidden: isEmbedded },
    { icon: Setting2, key: 'general', href: ROUTES.MY.SETTINGS.GENERAL.$path() },
  ];

  const handleSignOut = () =>
    makePrompt({
      key: 'sign-out',
      type: 'dialog',
      title: t('general-settings.log-out-confirm'),
      promise: () => signOut({}),
    });

  const isExactMatchIndex = useMatch({ path: ROUTES.MY.$path(), end: true });

  useEffect(() => {
    if (isDesktop && isExactMatchIndex) {
      navigate(ROUTES.MY.INFO.$path(), { replace: true });
    }
  }, [isDesktop, isExactMatchIndex]);

  return (
    <PageLayout
      title={t('mine.title')}
      headerConfig={{
        navigateBack: isEmbedded,
        headerClassname: 'text-white',
        className: 'desktop:hidden bg-transparent absolute top-0 left-0 right-0 z-40',
        elementBeforeNav: (
          <LoadingLayout
            className='min-h-42'
            promise={loaderData?.promises}>
            {() => (
              <div className='desktop:mx-4'>
                <UserHero
                  className='desktop:h-[144px]'
                  headImg={userInfo?.HeadImg}
                  nickname={userInfo?.NickName}
                  sign={userInfo?.Sign}
                  addOn={
                    <>
                      <Link
                        to={ROUTES.MY.INFO.$path()}
                        className='btn btn-square btn-ghost'>
                        <Edit2
                          variant='Bold'
                          className='size-5 text-white'
                          size={20}
                        />
                      </Link>
                      <Link
                        to={ROUTES.MY.QR_CODE.$path()}
                        onClick={(e) => {
                          if (isDesktop) {
                            e.preventDefault();
                            qrCodeModal.current?.open();
                          }
                        }}
                        className='btn btn-square btn-ghost'>
                        <QrCodeImage className='size-5' />
                      </Link>
                      <QrCodeModal ref={qrCodeModal} />
                    </>
                  }
                />
              </div>
            )}
          </LoadingLayout>
        ),
      }}
      className='gap-0 flex size-full desktop:flex-1 pb-safe-offset-16 desktop:pb-0'>
      <div className='desktop:mx-4 flex flex-col gap-3'>
        <div className='flex flex-col'>
          {menuItems
            .filter((item) => item.hidden !== true)
            .map((item) => (
              <Link
                key={item.key}
                to={item.href}>
                <AppCell
                  className='px-4 py-2 desktop:h-12'
                  label={t(`mine.${item.key}`)}
                  prefix={
                    <item.icon
                      variant='Outline'
                      size={20}
                      className='size-8 rounded-lg p-1.5 desktop:p-0 desktop:size-5 '
                    />
                  }
                  suffix={
                    <ArrowRight2
                      size={16}
                      variant='Outline'
                      className='size-4 desktop:hidden'
                    />
                  }
                />
              </Link>
            ))}

          {!isEmbedded && (
            <>
              <hr className='h-2 bg-secondary desktop:h-0' />
              {isDesktop && (
                <Link to={ROUTES.AUTH.SWITCH.$path()}>
                  <AppCell
                    className='px-0 py-2 desktop:h-12'
                    label={t('general-settings.switch-user')}
                    prefix={
                      <ProgrammingArrows
                        variant='Outline'
                        size={20}
                        className='size-8 rounded-lg p-1.5 desktop:p-0 desktop:size-5'
                      />
                    }
                  />
                </Link>
              )}

              <AppCell
                className='px-4 py-2 text-error desktop:h-12 desktop:mt-2'
                label={t('general-settings.log-out')}
                prefix={
                  <Logout
                    variant='Outline'
                    size={20}
                    className='size-8 rounded-lg p-1.5 desktop:p-0 desktop:size-5'
                  />
                }
                onClick={handleSignOut}
              />
            </>
          )}
        </div>
      </div>
    </PageLayout>
  );
}
